import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MapContainer } from 'components/map';
import { useMeetingDetail } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { getMapProvider } from 'common/utils/regionDetector';
import { isNativeApp, openNativeMapView } from 'common/webview';
import { AppScreen, Bleed, Button, PageIntro, Section } from 'components/ds';
import styles from './MeetingMap.module.css';

export const MeetingMap = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { meetingId } = useParams();
    const { data: meetingData } = useMeetingDetail();

    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
        }
    }, [meetingId]);

    // 해외 장소(네이티브 앱 내) → 자동으로 OS 구글지도 열고 이전 화면으로 복귀
    useEffect(() => {
        const lat = meetingData?.data?.latitude;
        const lng = meetingData?.data?.longitude;
        if (lat == null || lng == null) return;
        const center = { lat: Number(lat), lng: Number(lng) };
        if (getMapProvider(center) === 'google' && isNativeApp()) {
            /* 네이티브 브릿지는 lat/lng 을 읽습니다.
               latitude/longitude 로 보내고 있어서 좌표가 undefined 로 넘어갔고,
               해외 장소에서 네이티브 구글 지도가 빈 화면으로 떴습니다.
               `as any` 가 타입 검사까지 가리고 있었습니다. */
            openNativeMapView({
                name: meetingData.data.placeName || '',
                address: meetingData.data.placeAddress || '',
                lat: center.lat,
                lng: center.lng,
            });
            // 지도 전용 화면은 이제 필요 없음
            navigate(-1);
        }
    }, [meetingData, navigate]);

    const meeting = meetingData?.data ? {
        id: meetingData.data.meetingId,
        name: meetingData.data.meetingName,
        location: meetingData.data.placeName || '장소 미정',
        locationDetail: meetingData.data.placeAddress || '',
        latitude: meetingData.data.latitude,
        longitude: meetingData.data.longitude
    } : {
        id: meetingId,
        name: '로딩 중...',
        location: '장소 미정',
        locationDetail: '',
        latitude: null,
        longitude: null
    };

    const centerLat = meeting.latitude ? parseFloat(meeting.latitude) : 37.5665;
    const centerLng = meeting.longitude ? parseFloat(meeting.longitude) : 126.9780;

    // 외부 지도 앱 열기 URL
    const getExternalMapUrl = () => {
        const provider = getMapProvider({ lat: centerLat, lng: centerLng });
        if (provider === 'kakao') {
            // 카카오맵 앱/웹 열기
            return `https://map.kakao.com/link/map/${encodeURIComponent(meeting.location)},${centerLat},${centerLng}`;
        }
        // Google Maps
        if (meeting.latitude && meeting.longitude) {
            return `https://maps.google.com/?q=${meeting.latitude},${meeting.longitude}`;
        }
        return `https://maps.google.com/?q=${encodeURIComponent(meeting.locationDetail || meeting.location)}`;
    };

    const getExternalMapLabel = () => {
        const provider = getMapProvider({ lat: centerLat, lng: centerLng });
        return provider === 'kakao' ? t('meeting.openKakao') : t('meeting.openGoogle');
    };

    return (
        <AppScreen title={t('meeting.mapTitle')} bar="sub">
            <PageIntro title={meeting.name} desc={meeting.locationDetail} />

            <Section spacing="tight">
                <Bleed>
                    <MapContainer
                        center={{ lat: centerLat, lng: centerLng }}
                        markers={[{
                            id: 'meeting-place',
                            lat: centerLat,
                            lng: centerLng,
                            label: '',
                            tooltip: meeting.location,
                            isDestination: true,
                        }]}
                        height={400}
                        zoomLevel={3}
                        placeName={meeting.location}
                        placeAddress={meeting.locationDetail}
                    />
                </Bleed>
            </Section>

            <Section divided last>
                <div className={styles.actions}>
                    <Button
                        variant="primary"
                        size="lg"
                        block
                        onClick={() => window.open(getExternalMapUrl(), '_blank')}
                    >
                        {getExternalMapLabel()}
                    </Button>
                    <Button
                        variant="ghost"
                        size="lg"
                        block
                        onClick={() => navigate(`/meeting/detail/${meetingId}`)}
                    >
                        {t('meeting.backToDetail')}
                    </Button>
                </div>
            </Section>
        </AppScreen>
    );
};
